import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../data/site";
import { scrollToId, waLink } from "../../lib/contact";

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled ? "bg-navy/95 backdrop-blur-xl shadow-lg shadow-navy/30" : "bg-navy/80 backdrop-blur-md"
      } border-b border-white/5`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("home")} className="flex items-center group">
          <img src="/logo-full.png" alt="LaunchX Academy" className="h-8 sm:h-9 w-auto group-hover:scale-[1.03] transition-transform" data-testid="nav-logo-image" />
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-[12.5px] font-medium text-slate-300 hover:text-cyanx transition-colors tracking-wide"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-agency-link"
            href="/agency"
            className="text-[12.5px] font-bold text-cyanx border border-cyanx/40 hover:bg-cyanx/10 rounded-full px-4 py-2 transition-colors tracking-wide"
          >
            For Businesses
          </a>
          <a
            data-testid="nav-scholarship-link"
            href="/scholarship"
            className="text-[12.5px] font-bold text-cyanx border border-cyanx/40 hover:bg-cyanx/10 rounded-full px-4 py-2 transition-colors tracking-wide"
          >
            Scholarship Test
          </a>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="nav-whatsapp-button"
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            className="text-[13px] font-semibold text-slate-300 hover:text-white border border-white/15 hover:border-cyanx/50 rounded-full px-5 py-2.5 transition-colors"
          >
            WhatsApp Us
          </a>
          <button
            data-testid="nav-book-counselling-button"
            onClick={() => go("contact")}
            className="text-[13px] font-semibold text-white bg-electric hover:bg-electric-dark rounded-full px-5 py-2.5 transition-all hover:shadow-lg hover:shadow-electric/40 hover:-translate-y-0.5"
          >
            Book Free Counselling
          </button>
        </div>

        <button data-testid="nav-mobile-menu-button" onClick={() => setOpen(!open)} className="lg:hidden text-white p-2" aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-drawer"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-navy border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-lg font-semibold text-slate-200 hover:text-cyanx py-2.5 transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                data-testid="nav-mobile-book-button"
                onClick={() => go("contact")}
                className="mt-4 text-sm font-semibold text-white bg-electric rounded-full px-6 py-3.5 text-center"
              >
                Book Free Counselling
              </button>
              <a
                data-testid="nav-mobile-agency-link"
                href="/agency"
                className="mt-2 text-sm font-semibold text-cyanx border border-cyanx/40 rounded-full px-6 py-3.5 text-center"
              >
                LaunchX Agency — For Businesses
              </a>
              <a
                data-testid="nav-mobile-scholarship-link"
                href="/scholarship"
                className="mt-2 text-sm font-semibold text-cyanx border border-cyanx/40 rounded-full px-6 py-3.5 text-center"
              >
                Scholarship Test 2026
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
